/**
 * Slug of the `events_listing_page` record served at `/eventi`.
 * Every other record of the model is a curated selection under `/eventi/selezioni/`.
 *
 * This is a contract with the DatoCMS content: renaming that record's slug takes
 * `/eventi` down with it. The slug field presents every record under
 * `…/eventi/selezioni/`, so the main one is reachable there too — that URL
 * redirects to `/eventi` rather than serving the same listing twice.
 */
export const EVENTS_LISTING_MAIN_SLUG = 'eventi';

/**
 * Builds the website URL of an `events_listing_page` record.
 *
 * @param slug - The record slug from DatoCMS.
 * @returns `/eventi` for the main listing, `/eventi/selezioni/<slug>` for a selection,
 *          or `null` when the slug is missing.
 */
export function buildEventsListingUrl(slug: string | null | undefined): string | null {
	const normalized = slug?.trim();

	if (!normalized) {
		return null;
	}

	return normalized === EVENTS_LISTING_MAIN_SLUG ? '/eventi' : `/eventi/selezioni/${normalized}`;
}

/** A link to an events listing page, as the sitemap page renders it. */
export interface EventsListingLink {
	id: string;
	title: string;
	href: string;
}

/**
 * Turns events listing page records into links, dropping the ones flagged `noIndex`
 * so they are neither listed nor crawled from `/mappa-del-sito`.
 *
 * @param pages - Every events listing page record, `noIndex` ones included.
 * @returns The indexable links, main listing first and selections after it by title.
 */
export function toEventsListingLinks(
	pages: { title: string; slug: string; noIndex: boolean }[],
): EventsListingLink[] {
	return pages
		.flatMap((page) => {
			const href = page.noIndex ? null : buildEventsListingUrl(page.slug);

			return href ? [{ id: page.slug, title: page.title, href }] : [];
		})
		.sort((a, b) => {
			// The main listing heads the list; the selections follow it alphabetically.
			if (a.href === '/eventi') return -1;
			if (b.href === '/eventi') return 1;

			return a.title.localeCompare(b.title, 'it');
		});
}
